import React, { useMemo, useState } from 'react';
import type { AnalyticsData } from '../api/services/analyticsService';
import { VEHICLE_TYPE_META, VehicleTypeKey } from '../lib/vehicleCatalog';

/**
 * Mobilités les plus demandées (section Analytics), filtrables par type,
 * marque et modèle, en deux vues :
 *  - « Modèles » : marque + modèle regroupés — annonces publiées et en ligne,
 *    demandes de la période, terminées, taux d'annulation. Un modèle publié
 *    mais jamais demandé apparaît aussi (en bas) : c'est une information.
 *  - « Annonces » : chaque annonce demandée sur la période.
 *
 * L'API renvoie tout (plafonné à 500) : les filtres travaillent sur des
 * données complètes, pas sur un classement déjà tronqué.
 */

type View = 'models' | 'listings';

const PAGE = 8;
const nf = new Intl.NumberFormat('fr-FR');
const fmt = (n: number) => nf.format(Math.round(n || 0));
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const plural = (n: number, word: string) => `${word}${n > 1 ? 's' : ''}`;
const norm = (value: string | null | undefined) => String(value ?? '').trim().toLowerCase();
const typeLabel = (type: string, many = true) => {
  const meta = VEHICLE_TYPE_META[type as VehicleTypeKey];
  return meta ? (many ? meta.plural : meta.singular) : type;
};

const selectClass = 'min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400';

/** Taux d'annulation lisible : au-delà d'un tiers, on le signale. */
const CancelRate = ({ cancelled, requests }: { cancelled: number; requests: number }) => {
  if (requests === 0) return <span className="text-gray-400">—</span>;
  const rate = pct(cancelled, requests);
  return <span className={`tabular-nums ${rate >= 34 ? 'font-medium text-red-700' : 'text-gray-700'}`}>{rate} %</span>;
};

const MobilityRanking = ({ data, periodLabel }: { data: AnalyticsData; periodLabel: string }) => {
  const [view, setView] = useState<View>('models');
  const [type, setType] = useState('all');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [shown, setShown] = useState(PAGE);

  const types = useMemo(() => {
    const present = new Set(data.by_model.map((m) => m.vehicle_type));
    const order = data.by_type.map((t) => t.type).filter((t) => present.has(t));
    present.forEach((t) => { if (!order.includes(t)) order.push(t); });
    return order;
  }, [data]);

  const activeType = type !== 'all' && types.includes(type) ? type : 'all';
  const inType = <T extends { vehicle_type: string }>(row: T) => activeType === 'all' || row.vehicle_type === activeType;

  // Marques et modèles proposés : ceux du type choisi, triés par nom.
  const brands = useMemo(() => {
    const map = new Map<string, string>();
    data.by_model.filter(inType).forEach((m) => { if (m.brand && !map.has(norm(m.brand))) map.set(norm(m.brand), m.brand); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'fr'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeType]);
  const activeBrand = brand && brands.some(([key]) => key === brand) ? brand : '';

  const models = useMemo(() => {
    if (!activeBrand) return [];
    const map = new Map<string, string>();
    data.by_model.filter(inType).filter((m) => norm(m.brand) === activeBrand)
      .forEach((m) => { if (m.model && !map.has(norm(m.model))) map.set(norm(m.model), m.model); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'fr', { numeric: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeType, activeBrand]);
  const activeModel = model && models.some(([key]) => key === model) ? model : '';

  const matches = <T extends { vehicle_type: string; brand: string | null; model: string | null }>(row: T) =>
    inType(row) && (!activeBrand || norm(row.brand) === activeBrand) && (!activeModel || norm(row.model) === activeModel);

  const modelRows = data.by_model.filter(matches);
  const listingRows = data.top_vehicles.filter(matches);
  const rows = view === 'models' ? modelRows : listingRows;
  const filtered = activeType !== 'all' || !!activeBrand;

  const reset = (next: Partial<{ type: string; brand: string; model: string }>) => {
    if (next.type !== undefined) { setType(next.type); setBrand(''); setModel(''); }
    if (next.brand !== undefined) { setBrand(next.brand); setModel(''); }
    if (next.model !== undefined) setModel(next.model);
    setShown(PAGE);
  };

  // Bilan de la sélection, pour lire d'un coup d'œil ce que pèse le filtre.
  const totals = modelRows.reduce((acc, m) => ({
    listings: acc.listings + m.listings, requests: acc.requests + m.requests,
    completed: acc.completed + m.completed, cancelled: acc.cancelled + m.cancelled
  }), { listings: 0, requests: 0, completed: 0, cancelled: 0 });

  return (
    <div>
      {/* Vue + filtres */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Regrouper par" className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5 text-[13px] font-medium">
          {([['models', 'Modèles'], ['listings', 'Annonces']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              onClick={() => { setView(id); setShown(PAGE); }}
              className={`rounded-lg px-3.5 py-1.5 transition-colors ${view === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
          <select aria-label="Marque" value={activeBrand} onChange={(e) => reset({ brand: e.target.value })} className={selectClass}>
            <option value="">Toutes les marques</option>
            {brands.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select aria-label="Modèle" value={activeModel} disabled={!activeBrand} onChange={(e) => reset({ model: e.target.value })} className={selectClass}>
            <option value="">{activeBrand ? 'Tous les modèles' : 'Modèle (choisir une marque)'}</option>
            {models.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
      </div>

      {types.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par type de mobilité">
          {['all', ...types].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => reset({ type: t })}
              aria-pressed={activeType === t}
              className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${activeType === t ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {t === 'all' ? 'Toutes' : typeLabel(t)}
            </button>
          ))}
        </div>
      )}

      {filtered && (
        <p className="mt-3 flex flex-wrap items-center gap-x-2 text-[12px] text-gray-500">
          <span>
            Sélection : {fmt(totals.listings)} {plural(totals.listings, 'annonce')} · {fmt(totals.requests)} {plural(totals.requests, 'demande')} · {fmt(totals.completed)} {plural(totals.completed, 'terminée')}
            {totals.requests > 0 && <> · annulation <CancelRate cancelled={totals.cancelled} requests={totals.requests} /></>}
          </span>
          <button type="button" onClick={() => reset({ type: 'all' })} className="font-medium text-indigo-600 hover:text-indigo-800">Effacer les filtres</button>
        </p>
      )}

      {rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {view === 'listings' ? `Aucune annonce demandée sur les ${periodLabel} pour cette sélection.` : 'Aucun modèle pour cette sélection.'}
        </p>
      ) : view === 'models' ? (
        <>
          {/* Bureau : tableau */}
          <div className="mt-3 hidden md:block">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[12px] text-gray-500">
                  <th className="w-8 py-2 pr-2 font-medium" />
                  <th className="py-2 pr-3 font-medium">Modèle</th>
                  <th className="py-2 pr-3 text-right font-medium">Annonces</th>
                  <th className="py-2 pr-3 text-right font-medium">Demandes</th>
                  <th className="py-2 pr-3 text-right font-medium">Acceptées</th>
                  <th className="py-2 pr-3 text-right font-medium">Terminées</th>
                  <th className="py-2 text-right font-medium">Annulation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {modelRows.slice(0, shown).map((m, i) => (
                  <tr key={`${norm(m.brand)}|${norm(m.model)}`}>
                    <td className="py-2.5 pr-2 text-right text-[12px] tabular-nums text-gray-400">{i + 1}</td>
                    <td className="py-2.5 pr-3">
                      <span className="font-medium text-gray-900">{[m.brand, m.model].filter(Boolean).join(' ') || 'Sans nom'}</span>
                      <span className="ml-2 text-[12px] text-gray-500">{typeLabel(m.vehicle_type, false)}</span>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{fmt(m.online)}<span className="text-gray-400"> / {fmt(m.listings)}</span></td>
                    <td className="py-2.5 pr-3 text-right font-medium tabular-nums text-gray-900">{fmt(m.requests)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{m.requests > 0 ? `${pct(m.accepted, m.requests)} %` : '—'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{fmt(m.completed)}</td>
                    <td className="py-2.5 text-right"><CancelRate cancelled={m.cancelled} requests={m.requests} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11.5px] text-gray-400">Annonces : en ligne / publiées aujourd’hui. Demandes, acceptées, terminées, annulation : demandes déposées sur les {periodLabel}.</p>
          </div>
          {/* Mobile : lignes compactes */}
          <ol className="mt-3 divide-y divide-gray-100 md:hidden">
            {modelRows.slice(0, shown).map((m, i) => (
              <li key={`${norm(m.brand)}|${norm(m.model)}`} className="flex items-start gap-3 py-2.5">
                <span className="w-5 shrink-0 pt-0.5 text-right text-[12px] tabular-nums text-gray-400">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium text-gray-900">{[m.brand, m.model].filter(Boolean).join(' ') || 'Sans nom'}</p>
                  <p className="text-[12px] text-gray-500">
                    {typeLabel(m.vehicle_type, false)} · {fmt(m.online)}/{fmt(m.listings)} en ligne
                    {m.requests > 0 && <> · annulation <CancelRate cancelled={m.cancelled} requests={m.requests} /></>}
                  </p>
                </div>
                <div className="shrink-0 text-right text-[12px] leading-5 text-gray-500">
                  <p><span className="font-medium tabular-nums text-gray-900">{fmt(m.requests)}</span> {plural(m.requests, 'demande')}</p>
                  <p className="tabular-nums">{fmt(m.completed)} {plural(m.completed, 'terminée')}</p>
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <ol className="mt-3 divide-y divide-gray-50 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:divide-y-0">
          {listingRows.slice(0, shown).map((v, i) => (
            <li key={v.id_vehicle} className="flex items-start gap-3 border-gray-50 py-2.5 lg:border-b">
              <span className="w-5 shrink-0 pt-0.5 text-right text-[12px] tabular-nums text-gray-400">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="flex min-w-0 items-center gap-2 text-[13.5px] font-medium text-gray-900">
                  <span className="truncate">{[v.brand, v.model].filter(Boolean).join(' ') || typeLabel(v.vehicle_type, false)}</span>
                  {(v.is_suspended || !v.is_available) && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-medium text-gray-600">{v.is_suspended ? 'Suspendue' : 'En pause'}</span>
                  )}
                </p>
                <p className="truncate text-[12px] text-gray-500">
                  {typeLabel(v.vehicle_type, false)}{v.owner_name ? ` · ${v.owner_name}` : ''}
                  {v.requests > 0 && <> · annulation <CancelRate cancelled={v.cancelled} requests={v.requests} /></>}
                </p>
              </div>
              <div className="shrink-0 text-right text-[12px] leading-5 text-gray-500">
                <p><span className="font-medium tabular-nums text-gray-900">{fmt(v.requests)}</span> {plural(v.requests, 'demande')}</p>
                <p className="tabular-nums">{fmt(v.completed)} {plural(v.completed, 'terminée')}{v.viewers > 0 ? ` · ${fmt(v.viewers)} ${plural(v.viewers, 'consultée')}` : ''}</p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {rows.length > shown && (
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={() => setShown((n) => n + PAGE * 2)} className="text-xs font-medium px-3 py-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50">
            Afficher plus ({fmt(rows.length - shown)} {plural(rows.length - shown, 'autre')})
          </button>
        </div>
      )}
    </div>
  );
};

export default MobilityRanking;
